import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

import type {
  InvitationOpeningExperienceRecord,
  InvitationOpeningExperienceType,
} from '@tov-plus/domain';

import { msg } from '../../localization/i18n';
import { TovButton } from '../TovButton/TovButton';

export interface OpeningExperienceInvitationData {
  title: string;
  dateLabel: string;
  locationLabel: string;
  themeLabel: string;
}

export interface OpeningExperiencePresetMetadata {
  durationMs: number;
  emotionalCharacter: string;
  finalState: string;
  formatConstraints: string;
  motionSequence: string;
  reducedMotion: string;
  suitableFor: string;
}

export interface OpeningExperienceDefinition {
  type: InvitationOpeningExperienceType;
  name: string;
  description: string;
  availableInPhaseOne: boolean;
  interactive: boolean;
  productionReady: boolean;
  recommendedDefault: boolean;
  metadata: OpeningExperiencePresetMetadata;
}

export interface InvitationAnimationDebugController {
  getCurrentTime(): number;
  getDuration(): number;
  getState(): {
    phase: 'idle' | 'playing' | 'paused' | 'complete';
    preset: InvitationOpeningExperienceType;
    progress: number;
  };
  pause(): void;
  play(): void;
  restart(): void;
  seek(timeMs: number): void;
  setPlaybackRate(rate: number): void;
}

const productionPresetTypes: InvitationOpeningExperienceType[] = [
  'classic-reveal',
  'envelope-open',
  'soft-unfold',
  'elegant-lift',
  'simple-fade',
];

const legacyPresetFallbacks: Partial<
  Record<InvitationOpeningExperienceType, InvitationOpeningExperienceType>
> = {
  envelope: 'envelope-open',
  fade: 'simple-fade',
  'folded-card': 'soft-unfold',
  'interactive-envelope': 'envelope-open',
  'royal-gates': 'classic-reveal',
  storybook: 'soft-unfold',
  'wax-seal': 'classic-reveal',
};

const openingExperienceDefinitions: OpeningExperienceDefinition[] = [
  {
    type: 'none',
    name: 'None',
    get description() {
      return msg('ui.opening_experience.none_description');
    },
    availableInPhaseOne: true,
    interactive: false,
    productionReady: true,
    recommendedDefault: false,
    metadata: {
      durationMs: 0,
      emotionalCharacter: 'Immediate and utilitarian.',
      finalState: 'Invitation is shown without a transitional opening.',
      formatConstraints: 'Works for every fixture.',
      motionSequence: 'No motion.',
      reducedMotion: 'Identical to the standard behavior.',
      suitableFor: 'Operational previews and hosts who disable opening motion.',
    },
  },
  {
    type: 'classic-reveal',
    name: 'Classic Reveal',
    get description() {
      return msg('ui.opening_experience.classic_reveal_description');
    },
    availableInPhaseOne: true,
    interactive: false,
    productionReady: true,
    recommendedDefault: true,
    metadata: {
      durationMs: 1120,
      emotionalCharacter: 'Calm, premium, and versatile.',
      finalState: 'The same invitation surface settles at full opacity.',
      formatConstraints: 'Works for portrait, landscape, square, LTR, and RTL.',
      motionSequence:
        'Soft anticipation, shallow lift, content reveal, and shadow settle.',
      reducedMotion: 'A short opacity reveal with no travel or scale.',
      suitableFor: 'Default wedding and formal occasion invitations.',
    },
  },
  {
    type: 'envelope-open',
    name: 'Envelope Open',
    get description() {
      return msg('ui.opening_experience.envelope_open_description');
    },
    availableInPhaseOne: true,
    interactive: false,
    productionReady: true,
    recommendedDefault: false,
    metadata: {
      durationMs: 1380,
      emotionalCharacter: 'Ceremonial, warm, and still restrained.',
      finalState: 'Envelope layers fade after the paper reaches final scale.',
      formatConstraints: 'Best with portrait or formal card-like artwork.',
      motionSequence:
        'Envelope rests, flap opens around a top hinge, invitation lifts, envelope recedes.',
      reducedMotion: 'Envelope is omitted and the invitation fades in quickly.',
      suitableFor: 'Traditional wedding invitations and formal announcements.',
    },
  },
  {
    type: 'soft-unfold',
    name: 'Soft Unfold',
    get description() {
      return msg('ui.opening_experience.soft_unfold_description');
    },
    availableInPhaseOne: true,
    interactive: false,
    productionReady: true,
    recommendedDefault: false,
    metadata: {
      durationMs: 1240,
      emotionalCharacter: 'Refined, gentle, and editorial.',
      finalState:
        'Fold hint disappears as the single invitation surface settles.',
      formatConstraints: 'Works for square, portrait, and text-heavy layouts.',
      motionSequence:
        'Narrow folded form expands, crease softens, content reaches readable scale.',
      reducedMotion: 'Single short opacity reveal with stable dimensions.',
      suitableFor: 'Save-the-dates, itinerary cards, and softer themes.',
    },
  },
  {
    type: 'elegant-lift',
    name: 'Elegant Lift',
    get description() {
      return msg('ui.opening_experience.elegant_lift_description');
    },
    availableInPhaseOne: true,
    interactive: false,
    productionReady: true,
    recommendedDefault: false,
    metadata: {
      durationMs: 980,
      emotionalCharacter: 'Modern, direct, and polished.',
      finalState:
        'Invitation lands without overshoot and remains fully usable.',
      formatConstraints: 'Works especially well for dark or modern artwork.',
      motionSequence:
        'Invitation rises from a quiet shadow plane, fades in, and settles.',
      reducedMotion: 'Fast opacity transition with no lift.',
      suitableFor:
        'Modern weddings, black tie events, and professional occasions.',
    },
  },
  {
    type: 'simple-fade',
    name: 'Simple Fade',
    get description() {
      return msg('ui.opening_experience.simple_fade_description');
    },
    availableInPhaseOne: true,
    interactive: false,
    productionReady: true,
    recommendedDefault: false,
    metadata: {
      durationMs: 620,
      emotionalCharacter: 'Minimal and accessible.',
      finalState: 'Invitation is immediately legible after a brief fade.',
      formatConstraints: 'Works for every fixture.',
      motionSequence: 'Opacity reveal with a very small depth settle.',
      reducedMotion: 'Same choreography shortened to 220ms.',
      suitableFor:
        'Guests sensitive to motion or highly functional invitations.',
    },
  },
  {
    type: 'fade',
    name: 'Fade In',
    get description() {
      return msg('ui.opening_experience.fade_description');
    },
    availableInPhaseOne: false,
    interactive: false,
    productionReady: false,
    recommendedDefault: false,
    metadata: {
      durationMs: 620,
      emotionalCharacter: 'Legacy fallback.',
      finalState: 'Maps to Simple Fade.',
      formatConstraints: 'Legacy only.',
      motionSequence: 'Legacy type rendered by Simple Fade.',
      reducedMotion: 'Maps to Simple Fade reduced motion.',
      suitableFor: 'Existing stored invitations.',
    },
  },
  {
    type: 'envelope',
    name: 'Envelope Reveal',
    get description() {
      return msg('ui.opening_experience.envelope_description');
    },
    availableInPhaseOne: false,
    interactive: false,
    productionReady: false,
    recommendedDefault: false,
    metadata: {
      durationMs: 1380,
      emotionalCharacter: 'Legacy fallback.',
      finalState: 'Maps to Envelope Open.',
      formatConstraints: 'Legacy only.',
      motionSequence: 'Legacy type rendered by Envelope Open.',
      reducedMotion: 'Maps to Envelope Open reduced motion.',
      suitableFor: 'Existing stored invitations.',
    },
  },
  {
    type: 'interactive-envelope',
    name: 'Interactive Envelope',
    get description() {
      return msg('ui.opening_experience.interactive_envelope_description');
    },
    availableInPhaseOne: false,
    interactive: false,
    productionReady: false,
    recommendedDefault: false,
    metadata: {
      durationMs: 1380,
      emotionalCharacter: 'Legacy fallback.',
      finalState: 'Maps to Envelope Open.',
      formatConstraints: 'Legacy only.',
      motionSequence: 'Legacy type rendered by Envelope Open.',
      reducedMotion: 'Maps to Envelope Open reduced motion.',
      suitableFor: 'Existing stored invitations.',
    },
  },
  {
    type: 'folded-card',
    name: 'Folded Card',
    get description() {
      return msg('ui.opening_experience.folded_card_description');
    },
    availableInPhaseOne: false,
    interactive: false,
    productionReady: false,
    recommendedDefault: false,
    metadata: {
      durationMs: 1240,
      emotionalCharacter: 'Legacy fallback.',
      finalState: 'Maps to Soft Unfold.',
      formatConstraints: 'Legacy only.',
      motionSequence: 'Legacy type rendered by Soft Unfold.',
      reducedMotion: 'Maps to Soft Unfold reduced motion.',
      suitableFor: 'Existing stored invitations.',
    },
  },
];

export function getOpeningExperienceDefinitions() {
  return openingExperienceDefinitions;
}

export function getProductionOpeningExperienceDefinitions() {
  return openingExperienceDefinitions.filter(
    (definition) =>
      definition.productionReady &&
      productionPresetTypes.includes(definition.type),
  );
}

export function getOpeningExperienceDefinition(
  type: InvitationOpeningExperienceType,
): OpeningExperienceDefinition {
  return (
    openingExperienceDefinitions.find(
      (definition) => definition.type === type,
    ) ?? getOpeningExperienceDefinition('classic-reveal')
  );
}

function resolvePresetType(type: InvitationOpeningExperienceType) {
  return legacyPresetFallbacks[type] ?? type;
}

function getPresetDuration(
  type: InvitationOpeningExperienceType,
  configuration?: Record<string, unknown>,
) {
  const configuredDuration = configuration?.durationMs;
  if (typeof configuredDuration === 'number' && configuredDuration >= 200) {
    return configuredDuration;
  }

  return getOpeningExperienceDefinition(resolvePresetType(type)).metadata
    .durationMs;
}

function clampProgress(value: number) {
  return Math.min(1, Math.max(0, value));
}

function interpolate(progress: number, from: number, to: number) {
  return from + (to - from) * progress;
}

function delayedProgress(progress: number, delay: number, span: number) {
  return clampProgress((progress - delay) / span);
}

function easeOutCubic(progress: number) {
  return 1 - Math.pow(1 - clampProgress(progress), 3);
}

function easeInOutCubic(progress: number) {
  const clamped = clampProgress(progress);
  return clamped < 0.5
    ? 4 * clamped * clamped * clamped
    : 1 - Math.pow(-2 * clamped + 2, 3) / 2;
}

function resolveMotionVariables(input: {
  progress: number;
  reducedMotion: boolean;
  type: InvitationOpeningExperienceType;
}) {
  const { progress, reducedMotion, type } = input;
  const eased = easeOutCubic(progress);
  const reveal = reducedMotion
    ? progress
    : easeOutCubic(delayedProgress(progress, 0.08, 0.82));
  const variables = {
    '--tov-opening-clip-bottom': '0%',
    '--tov-opening-clip-left': '0%',
    '--tov-opening-clip-right': '0%',
    '--tov-opening-clip-top': '0%',
    '--tov-opening-envelope-opacity': '0',
    '--tov-opening-envelope-front-z': '48px',
    '--tov-opening-envelope-scale': '0.92',
    '--tov-opening-envelope-y': '2rem',
    '--tov-opening-envelope-z': '0px',
    '--tov-opening-flap-opacity': '1',
    '--tov-opening-flap-rotate': '-168deg',
    '--tov-opening-fold-opacity': '0',
    '--tov-opening-fold-rotate-y': '0deg',
    '--tov-opening-fold-scale-x': '1',
    '--tov-opening-invitation-opacity': String(reveal),
    '--tov-opening-invitation-rotate-x': '0deg',
    '--tov-opening-invitation-rotate-y': '0deg',
    '--tov-opening-invitation-rotate-z': '0deg',
    '--tov-opening-invitation-scale': String(interpolate(eased, 0.965, 1)),
    '--tov-opening-invitation-scale-x': '1',
    '--tov-opening-invitation-y': `${interpolate(eased, 18, 0)}px`,
    '--tov-opening-invitation-z': '0px',
    '--tov-opening-shadow-opacity': String(interpolate(eased, 0.12, 0.24)),
    '--tov-opening-shadow-scale': String(interpolate(eased, 0.9, 1)),
  };

  if (reducedMotion || type === 'none') {
    return {
      ...variables,
      '--tov-opening-invitation-opacity':
        type === 'none' ? '1' : String(progress),
      '--tov-opening-invitation-scale': '1',
      '--tov-opening-invitation-y': '0px',
      '--tov-opening-shadow-opacity': '0.2',
      '--tov-opening-shadow-scale': '1',
    };
  }

  if (type === 'classic-reveal') {
    const open = easeOutCubic(delayedProgress(progress, 0.02, 0.76));
    const reveal = easeOutCubic(delayedProgress(progress, 0.06, 0.66));

    return {
      ...variables,
      '--tov-opening-clip-bottom': `${interpolate(open, 14, 0)}%`,
      '--tov-opening-clip-top': `${interpolate(open, 14, 0)}%`,
      '--tov-opening-invitation-opacity': String(reveal),
      '--tov-opening-invitation-scale': String(interpolate(open, 0.94, 1)),
      '--tov-opening-invitation-y': `${interpolate(open, 30, 0)}px`,
      '--tov-opening-shadow-opacity': String(interpolate(open, 0.08, 0.24)),
      '--tov-opening-shadow-scale': String(interpolate(open, 0.78, 1)),
    };
  }

  if (type === 'envelope-open') {
    const flap = easeOutCubic(delayedProgress(progress, 0.04, 0.28));
    const slideOut = easeOutCubic(delayedProgress(progress, 0.28, 0.34));
    const envelopeRecede = easeOutCubic(delayedProgress(progress, 0.62, 0.14));
    const slideDown = easeInOutCubic(delayedProgress(progress, 0.74, 0.16));
    const scaleUp = easeOutCubic(delayedProgress(progress, 0.88, 0.1));
    const flapSoften = easeOutCubic(delayedProgress(progress, 0.2, 0.38));
    const paperY = interpolate(slideDown, interpolate(slideOut, -8, -202), 0);

    return {
      ...variables,
      '--tov-opening-clip-bottom': '0%',
      '--tov-opening-envelope-opacity': String(1 - envelopeRecede),
      '--tov-opening-envelope-front-z': '0px',
      '--tov-opening-envelope-scale': String(
        interpolate(envelopeRecede, 1.02, 0.9),
      ),
      '--tov-opening-envelope-y': `${interpolate(slideDown, -22, -34)}px`,
      '--tov-opening-envelope-z': '0px',
      '--tov-opening-flap-opacity': String(interpolate(flapSoften, 1, 0.46)),
      '--tov-opening-flap-rotate': `${interpolate(flap, 0, -156)}deg`,
      '--tov-opening-invitation-opacity': '1',
      '--tov-opening-invitation-scale': String(interpolate(scaleUp, 0.5, 1)),
      '--tov-opening-invitation-y': `${paperY}px`,
      '--tov-opening-invitation-z': '0px',
      '--tov-opening-shadow-opacity': String(
        interpolate(slideDown, 0.08, 0.26),
      ),
      '--tov-opening-shadow-scale': String(interpolate(slideDown, 0.72, 1)),
    };
  }

  if (type === 'soft-unfold') {
    const open = easeInOutCubic(delayedProgress(progress, 0.03, 0.7));
    const settle = easeOutCubic(delayedProgress(progress, 0.45, 0.4));

    return {
      ...variables,
      '--tov-opening-clip-left': `${interpolate(open, 32, 0)}%`,
      '--tov-opening-clip-right': `${interpolate(open, 32, 0)}%`,
      '--tov-opening-fold-opacity': String(interpolate(open, 0.72, 0)),
      '--tov-opening-fold-rotate-y': `${interpolate(open, -18, 0)}deg`,
      '--tov-opening-fold-scale-x': String(interpolate(open, 0.28, 1.06)),
      '--tov-opening-invitation-opacity': String(
        easeOutCubic(delayedProgress(progress, 0.02, 0.46)),
      ),
      '--tov-opening-invitation-rotate-x': `${interpolate(open, 7, 0)}deg`,
      '--tov-opening-invitation-rotate-y': `${interpolate(open, -13, 0)}deg`,
      '--tov-opening-invitation-scale': String(interpolate(settle, 0.98, 1)),
      '--tov-opening-invitation-scale-x': String(interpolate(open, 0.64, 1)),
      '--tov-opening-invitation-y': `${interpolate(settle, 10, 0)}px`,
      '--tov-opening-shadow-opacity': String(interpolate(open, 0.1, 0.22)),
      '--tov-opening-shadow-scale': String(interpolate(open, 0.54, 1)),
    };
  }

  if (type === 'elegant-lift') {
    const lift = easeOutCubic(delayedProgress(progress, 0, 0.78));
    const reveal = easeOutCubic(delayedProgress(progress, 0, 0.52));

    return {
      ...variables,
      '--tov-opening-invitation-opacity': String(reveal),
      '--tov-opening-invitation-rotate-x': `${interpolate(lift, 5, 0)}deg`,
      '--tov-opening-invitation-scale': String(interpolate(lift, 0.985, 1)),
      '--tov-opening-invitation-y': `${interpolate(lift, 76, 0)}px`,
      '--tov-opening-invitation-z': `${interpolate(lift, -46, 0)}px`,
      '--tov-opening-shadow-opacity': String(interpolate(lift, 0.3, 0.18)),
      '--tov-opening-shadow-scale': String(interpolate(lift, 0.58, 1)),
    };
  }

  if (type === 'simple-fade') {
    return {
      ...variables,
      '--tov-opening-invitation-opacity': String(progress),
      '--tov-opening-invitation-scale': String(interpolate(eased, 0.99, 1)),
      '--tov-opening-invitation-y': '0px',
      '--tov-opening-shadow-opacity': String(interpolate(eased, 0.16, 0.22)),
    };
  }

  return variables;
}

export function OpeningExperiencePlayer({
  allowSkip = true,
  configuration,
  debugControllerId,
  experienceType,
  invitationData,
  onComplete,
  onSkip,
  previewMode = false,
  replayKey = 0,
}: {
  allowSkip?: boolean;
  configuration?: Record<string, unknown>;
  debugControllerId?: string;
  experienceType: InvitationOpeningExperienceType;
  invitationData: OpeningExperienceInvitationData;
  onComplete?(): void;
  onSkip?(): void;
  previewMode?: boolean;
  replayKey?: number;
}) {
  const reducedByConfig = configuration?.reducedMotionPreview === true;
  const playbackRate =
    typeof configuration?.playbackRate === 'number' &&
    configuration.playbackRate > 0
      ? configuration.playbackRate
      : 1;
  const resolvedType = resolvePresetType(experienceType);
  const baseDuration = getPresetDuration(experienceType, configuration);
  const reducedMotion =
    reducedByConfig ||
    (typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const durationMs =
    experienceType === 'none'
      ? 0
      : reducedMotion
        ? Math.min(260, baseDuration)
        : baseDuration;
  const [currentTime, setCurrentTime] = useState(durationMs);
  const [phase, setPhase] = useState<
    'idle' | 'playing' | 'paused' | 'complete'
  >(durationMs === 0 ? 'complete' : 'idle');
  const rafRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const pausedAtRef = useRef(0);
  const completedRef = useRef(false);

  const progress =
    durationMs === 0 ? 1 : clampProgress(currentTime / durationMs);
  const definition = getOpeningExperienceDefinition(resolvedType);

  const complete = useCallback(() => {
    setCurrentTime(durationMs);
    setPhase('complete');
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [durationMs, onComplete]);

  const stopAnimationFrame = useCallback(() => {
    if (rafRef.current !== null) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const playFrom = useCallback(
    (timeMs: number) => {
      if (durationMs === 0) {
        complete();
        return;
      }

      stopAnimationFrame();
      setPhase('playing');
      startedAtRef.current = performance.now() - timeMs / playbackRate;

      const tick = (timestamp: number) => {
        const elapsed = (timestamp - startedAtRef.current) * playbackRate;
        const nextTime = Math.min(durationMs, elapsed);
        setCurrentTime(nextTime);

        if (nextTime >= durationMs) {
          rafRef.current = null;
          complete();
          return;
        }

        rafRef.current = window.requestAnimationFrame(tick);
      };

      rafRef.current = window.requestAnimationFrame(tick);
    },
    [complete, durationMs, playbackRate, stopAnimationFrame],
  );

  const restart = useCallback(() => {
    completedRef.current = false;
    setCurrentTime(0);
    playFrom(0);
  }, [playFrom]);

  const pause = useCallback(() => {
    stopAnimationFrame();
    pausedAtRef.current = currentTime;
    setPhase(currentTime >= durationMs ? 'complete' : 'paused');
  }, [currentTime, durationMs, stopAnimationFrame]);

  const play = useCallback(() => {
    playFrom(pausedAtRef.current || currentTime);
  }, [currentTime, playFrom]);

  const seek = useCallback(
    (timeMs: number) => {
      const nextTime = Math.min(durationMs, Math.max(0, timeMs));
      stopAnimationFrame();
      completedRef.current = nextTime >= durationMs;
      pausedAtRef.current = nextTime;
      setCurrentTime(nextTime);
      setPhase(nextTime >= durationMs ? 'complete' : 'paused');
    },
    [durationMs, stopAnimationFrame],
  );

  useEffect(() => {
    completedRef.current = false;
    setCurrentTime(durationMs === 0 ? 0 : 0);

    if (durationMs === 0) {
      complete();
      return undefined;
    }

    playFrom(0);
    return stopAnimationFrame;
  }, [
    complete,
    durationMs,
    experienceType,
    playFrom,
    replayKey,
    stopAnimationFrame,
  ]);

  useEffect(() => {
    if (!debugControllerId || typeof window === 'undefined') {
      return undefined;
    }

    const controller: InvitationAnimationDebugController = {
      getCurrentTime: () => currentTime,
      getDuration: () => durationMs,
      getState: () => ({
        phase,
        preset: resolvedType,
        progress,
      }),
      pause,
      play,
      restart,
      seek,
      setPlaybackRate: () => undefined,
    };
    const debugWindow = window as Window & {
      __tovInvitationAnimationControllers?: Record<
        string,
        InvitationAnimationDebugController
      >;
    };
    debugWindow.__tovInvitationAnimationControllers ??= {};
    debugWindow.__tovInvitationAnimationControllers[debugControllerId] =
      controller;

    return () => {
      delete debugWindow.__tovInvitationAnimationControllers?.[
        debugControllerId
      ];
    };
  }, [
    currentTime,
    debugControllerId,
    durationMs,
    pause,
    phase,
    play,
    progress,
    resolvedType,
    restart,
    seek,
  ]);

  const canSkip =
    allowSkip &&
    !previewMode &&
    !reducedMotion &&
    experienceType !== 'none' &&
    phase !== 'complete';
  const dateParts = useMemo(
    () => invitationData.dateLabel.split(',').map((part) => part.trim()),
    [invitationData.dateLabel],
  );
  const motionVariables = resolveMotionVariables({
    progress,
    reducedMotion,
    type: resolvedType,
  });

  return (
    <div
      aria-label={
        previewMode
          ? msg('ui.opening_experience.preview_label')
          : msg('ui.opening_experience.player_label')
      }
      className="tov-opening-experience"
      data-phase={phase}
      data-preset={resolvedType}
      data-reduced-motion={reducedMotion ? 'true' : 'false'}
      data-testid="tov-opening-experience-player"
      style={
        {
          '--tov-opening-duration-ms': durationMs,
          '--tov-opening-progress': progress,
          ...motionVariables,
        } as CSSProperties
      }
    >
      {previewMode || canSkip ? (
        <div className="tov-opening-experience__header">
          {previewMode ? (
            <div>
              <strong>{definition.name}</strong>
              <p>{definition.description}</p>
            </div>
          ) : (
            <span />
          )}
          {canSkip ? (
            <TovButton
              onClick={() => {
                stopAnimationFrame();
                complete();
                onSkip?.();
              }}
              type="button"
              variant="secondary"
            >
              {msg('ui.opening_experience.skip_animation')}
            </TovButton>
          ) : null}
        </div>
      ) : null}

      <div className="tov-opening-experience__stage">
        <EnvelopeBackProp />
        <div className="tov-opening-experience__shadow" aria-hidden="true" />
        <InvitationSurface
          dateParts={dateParts}
          invitationData={invitationData}
        />
        <EnvelopeFrontProp />
        <div className="tov-opening-experience__fold" aria-hidden="true" />
      </div>
    </div>
  );
}

function EnvelopeBackProp() {
  return (
    <div
      className="tov-opening-envelope tov-opening-envelope--back-layer"
      aria-hidden="true"
    >
      <div className="tov-opening-envelope__back" />
      <div className="tov-opening-envelope__flap" />
    </div>
  );
}

function EnvelopeFrontProp() {
  return (
    <div
      className="tov-opening-envelope tov-opening-envelope--front-layer"
      aria-hidden="true"
    >
      <div className="tov-opening-envelope__front" />
    </div>
  );
}

export function InvitationSurface({
  dateParts,
  invitationData,
}: {
  dateParts?: string[];
  invitationData: OpeningExperienceInvitationData;
}) {
  const resolvedDateParts =
    dateParts ?? invitationData.dateLabel.split(',').map((part) => part.trim());

  return (
    <article
      className="tov-opening-experience-invitation"
      data-testid="tov-opening-experience-invitation"
    >
      <div className="tov-opening-experience-invitation__inner">
        <p className="tov-opening-experience-invitation__theme">
          {invitationData.themeLabel}
        </p>
        <div
          className="tov-opening-experience-invitation__rule"
          aria-hidden="true"
        />
        <h2>{invitationData.title}</h2>
        <div className="tov-opening-experience-invitation__details">
          <strong>{resolvedDateParts[0] ?? invitationData.dateLabel}</strong>
          {resolvedDateParts[1] ? (
            <span>{resolvedDateParts.slice(1).join(', ')}</span>
          ) : null}
          <span>{invitationData.locationLabel}</span>
        </div>
      </div>
    </article>
  );
}

export function buildOpeningExperiencePreviewData(input?: {
  title?: string | null;
  dateLabel?: string | null;
  locationLabel?: string | null;
  themeLabel?: string | null;
}): OpeningExperienceInvitationData {
  return {
    title: input?.title?.trim() || 'Zvi & Zavi',
    dateLabel: input?.dateLabel?.trim() || 'September 15, 2027',
    locationLabel: input?.locationLabel?.trim() || 'King David Hotel',
    themeLabel: input?.themeLabel?.trim() || 'Royal Gold',
  };
}

export function serializeOpeningExperience(
  openingExperience: InvitationOpeningExperienceRecord,
) {
  return {
    type: openingExperience.type,
    configuration: { ...openingExperience.configuration },
    show_on_first_visit_only: openingExperience.showOnFirstVisitOnly,
    allow_skip: openingExperience.allowSkip,
  };
}
